import { ClientInfo, ProductRecommendation, Answer, Question } from '../types';

interface SavedResults {
  id: string;
  date: Date;
  clientInfo: ClientInfo;
  recommendedProducts: ProductRecommendation[];
  bonusProducts: ProductRecommendation[];
  answers: Record<string, Answer>;
  questions: Question[];
}

const STORAGE_KEY = 'pemlab_results';

export class StorageService {
  static saveResults(
    clientInfo: ClientInfo,
    recommendedProducts: ProductRecommendation[],
    bonusProducts: ProductRecommendation[],
    answers: Map<string, Answer>,
    questions: Question[]
  ): string {
    const resultId = this.generateResultId();
    const savedResults: SavedResults = {
      id: resultId,
      date: new Date(),
      clientInfo,
      recommendedProducts,
      bonusProducts,
      answers: Object.fromEntries(answers.entries()),
      questions
    };

    try {
      // Save to localStorage
      localStorage.setItem(`${STORAGE_KEY}_${resultId}`, JSON.stringify(savedResults));
      
      // Also save the current result ID for quick access
      localStorage.setItem(`${STORAGE_KEY}_current`, resultId);
      
      console.log('Results saved successfully with ID:', resultId);
      return resultId;
    } catch (error) {
      console.error('Error saving results:', error);
      throw new Error('Impossible de sauvegarder les résultats');
    }
  }

  static getResults(resultId?: string): SavedResults | null {
    try {
      // If no ID provided, get the current result
      const id = resultId || localStorage.getItem(`${STORAGE_KEY}_current`);
      if (!id) return null;

      const savedData = localStorage.getItem(`${STORAGE_KEY}_${id}`);
      if (!savedData) return null;

      const results = JSON.parse(savedData);
      console.log('Results loaded successfully:', results);
      return results;
    } catch (error) {
      console.error('Error loading results:', error);
      return null;
    }
  }

  static hasResults(): boolean {
    const currentId = localStorage.getItem(`${STORAGE_KEY}_current`);
    if (!currentId) return false;
    
    return localStorage.getItem(`${STORAGE_KEY}_${currentId}`) !== null;
  }

  static clearResults(): void {
    // Clear all results
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  }

  static getAllResultIds(): string[] {
    const ids: string[] = [];
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(`${STORAGE_KEY}_result_`)) {
        const id = key.replace(`${STORAGE_KEY}_`, '');
        ids.push(id);
      }
    });
    
    return ids;
  }

  private static generateResultId(): string {
    return `result_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
